/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo } from 'react';
import { Close } from '@mui/icons-material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Box,
  colors,
  FormControlLabel,
  Checkbox,
  Chip,
  FormHelperText,
  TextField,
} from '@mui/material';
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

import dayjs from 'dayjs';
import { DatePicker } from '../DatePicker';
import { useGetPublicMerchantQuery } from '../../../../store/modules/merchant/api';
import { useGetAllPaylinksQuery } from '../../../../store/modules/paylink/api';
import { CustomerAutoComplete, getOptionLabel } from '../../../../common/CustomerAutoComplete';
import { BaseFilterSection as Section, filterSelectAllRowSx } from '../BaseFiltersPanel';
import { getStatusLabel } from '../../constants';
import {
  getVisiblePaylinkPaymentMethodFilterTypesFromHistory,
  PAYLINK_FILTER_STATUSES,
  PaylinkFiltersFormValues,
  PaylinkPaymentMethodFilterType,
  shouldShowPaylinkPaymentMethodFilterSection,
} from '../../../../hooks/usePaylinkFiltersForm';
import flexPayLogo from '../../../../images/flex_pay/flex_pay_horizontal_logo.svg';

interface Props {
  merchantId: string;
  maxDateRange?: [number, dayjs.ManipulateType];
  requiredFields?: (keyof PaylinkFiltersFormValues)[];
  showTransactionDateRange?: boolean;
  hookFormProps: {
    watch: UseFormWatch<PaylinkFiltersFormValues>;
    setValue: UseFormSetValue<PaylinkFiltersFormValues>;
    control: Control<PaylinkFiltersFormValues>;
    errors: FieldErrors<PaylinkFiltersFormValues>;
  };
}

type DateRangeFieldNames =
  readonly ['createdAfter', 'createdBefore'] | readonly ['transactedAfter', 'transactedBefore'];

interface DateRangeSectionProps {
  title: string;
  names: DateRangeFieldNames;
  maxDateRange?: [number, dayjs.ManipulateType];
  hookFormProps: Props['hookFormProps'];
}

const DateRangeSection = ({ title, names, maxDateRange, hookFormProps }: DateRangeSectionProps) => {
  const { watch, setValue, control, errors } = hookFormProps;
  const [afterName, beforeName] = names;
  const after = watch(afterName);
  const before = watch(beforeName);

  // Auto-adjust end date
  useEffect(() => {
    if (!maxDateRange || !after || !before) return;

    const maxBeforeDate = dayjs(after)
      .add(...maxDateRange)
      .endOf('day');
    // if end date is out of range or before start date, auto set it to max range possible
    if (before.isAfter(maxBeforeDate) || before.isBefore(after)) {
      setValue(beforeName, maxBeforeDate);
    }
  }, [after, before, beforeName, maxDateRange, setValue]);

  return (
    <Section
      title={title}
      tooltip={
        maxDateRange ? `You can select a range of up to ${maxDateRange[0]} ${maxDateRange[1]}s` : ''
      }
    >
      <Box sx={{ display: 'flex', gap: 1 }}>
        {names.map((name) => (
          <Controller
            key={name}
            name={name}
            control={control}
            render={({ field }) => (
              <DatePicker
                key={name}
                label={name === afterName ? 'Start date' : 'End date'}
                errorMessage={errors[name]?.message}
                disablePast={false}
                dateOnly
                // open date picker in the same month as the other selected date if it exists
                referenceDate={(name === afterName ? before : after) ?? undefined}
                shouldDisableDate={(date) => {
                  if (!maxDateRange) return false;

                  // For end date picker
                  if (name === beforeName && after) {
                    const maxBeforeDate = dayjs(after).add(...maxDateRange);
                    return date.isAfter(maxBeforeDate) || date.isBefore(after); // disable dates after max range OR before start date
                  }

                  return false; // No restrictions on start date, we will auto adjust end date base on start date
                }}
                closeOnSelect
                {...field}
              />
            )}
          />
        ))}
      </Box>
    </Section>
  );
};

const getPaymentMethodFilterLabel = (paymentMethodType: PaylinkPaymentMethodFilterType) => {
  switch (paymentMethodType) {
    case 'card':
      return 'Card';
    case 'bank_account':
      return 'Bank Account';
    case 'flex_pay':
      return (
        <Box
          component="img"
          src={flexPayLogo}
          alt="FlexPay"
          sx={{ height: 16, width: 'auto', display: 'block' }}
        />
      );
    default:
      return paymentMethodType;
  }
};

export const PaylinkFilters = ({
  merchantId,
  maxDateRange,
  hookFormProps,
  requiredFields,
  showTransactionDateRange,
}: Props) => {
  const { watch, setValue, control, errors } = hookFormProps;
  const { paylinkPaymentMethodFilter } = useFlags();

  const { data: publicData } = useGetPublicMerchantQuery(merchantId, { skip: !merchantId });
  const acceptedCurrencySymbol = publicData?.acceptedCurrency?.symbol || '$';

  // Existence probes: pageSize 1 is enough to know if any matching paylinks exist.
  const skipPaymentMethodProbes = !merchantId || !paylinkPaymentMethodFilter;
  const { data: bankAccountProbe, isLoading: isLoadingBankProbe } = useGetAllPaylinksQuery(
    {
      merchantId,
      paymentMethodType: ['bank_account'],
      pageNumber: 1,
      pageSize: 1,
    },
    { skip: skipPaymentMethodProbes },
  );
  const { data: flexPayProbe, isLoading: isLoadingFlexPayProbe } = useGetAllPaylinksQuery(
    {
      merchantId,
      isFlexPay: true,
      pageNumber: 1,
      pageSize: 1,
    },
    { skip: skipPaymentMethodProbes },
  );

  const hasBankAccountPaylinks =
    (bankAccountProbe?.metadata.totalRecords ?? bankAccountProbe?.data?.length ?? 0) > 0;
  const hasFlexPayPaylinks =
    (flexPayProbe?.metadata.totalRecords ?? flexPayProbe?.data?.length ?? 0) > 0;

  const visiblePaymentMethodTypes = useMemo(
    () =>
      getVisiblePaylinkPaymentMethodFilterTypesFromHistory({
        hasBankAccountPaylinks,
        hasFlexPayPaylinks,
      }),
    [hasBankAccountPaylinks, hasFlexPayPaylinks],
  );

  const showPaymentMethodSection =
    paylinkPaymentMethodFilter &&
    !isLoadingBankProbe &&
    !isLoadingFlexPayProbe &&
    shouldShowPaylinkPaymentMethodFilterSection(visiblePaymentMethodTypes);

  const selectedStatuses = watch('status');
  const selectedPaymentMethodTypes = watch('paymentMethodType');
  const selectedCustomers = watch('customers');

  // Drop selections for payment methods the merchant cannot filter on
  useEffect(() => {
    if (!paylinkPaymentMethodFilter) return;

    const nextSelected = selectedPaymentMethodTypes.filter((type) =>
      visiblePaymentMethodTypes.includes(type),
    );
    if (nextSelected.length !== selectedPaymentMethodTypes.length) {
      setValue('paymentMethodType', nextSelected);
    }
  }, [paylinkPaymentMethodFilter, selectedPaymentMethodTypes, setValue, visiblePaymentMethodTypes]);

  return (
    <>
      <Section
        title="Status"
        required={requiredFields?.includes('status')}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          '& > *': {
            height: '24px',
            width: 'fit-content',
          },
          '& .MuiFormHelperText-root': {
            height: 'auto',
            width: '100%',
            alignSelf: 'stretch',
            mt: 0.5,
          },
        }}
      >
        <FormControlLabel
          sx={filterSelectAllRowSx}
          control={
            <Checkbox
              indeterminate={
                selectedStatuses.length > 0 &&
                selectedStatuses.length < PAYLINK_FILTER_STATUSES.length
              }
              checked={selectedStatuses.length === PAYLINK_FILTER_STATUSES.length}
              onChange={(e) =>
                e.target.checked
                  ? setValue('status', PAYLINK_FILTER_STATUSES as string[])
                  : setValue('status', [])
              }
            />
          }
          label="Select All"
        />
        {PAYLINK_FILTER_STATUSES.map((status) => (
          <FormControlLabel
            key={status}
            control={
              <Checkbox
                checked={selectedStatuses.includes(status)}
                onChange={(e) =>
                  e.target.checked
                    ? setValue('status', [...selectedStatuses, status])
                    : setValue(
                        'status',
                        selectedStatuses.filter((selectedStatus) => selectedStatus !== status),
                      )
                }
              />
            }
            label={getStatusLabel(status)}
          />
        ))}
        {errors.status && (
          <FormHelperText
            sx={{
              fontWeight: 700,
              lineHeight: '24px',
              color: 'error.main',
            }}
          >
            {errors.status.message}
          </FormHelperText>
        )}
      </Section>
      {showPaymentMethodSection && (
        <Section
          title="Payment method"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            '& > *': {
              height: '24px',
              width: 'fit-content',
            },
            // Helper text shouldn't inherit the fixed checkbox row height
            '& .MuiFormHelperText-root': {
              height: 'auto',
              mt: 0.5,
            },
          }}
        >
          <FormControlLabel
            sx={filterSelectAllRowSx}
            control={
              <Checkbox
                indeterminate={
                  selectedPaymentMethodTypes.length > 0 &&
                  selectedPaymentMethodTypes.length < visiblePaymentMethodTypes.length
                }
                checked={
                  selectedPaymentMethodTypes.length === visiblePaymentMethodTypes.length &&
                  visiblePaymentMethodTypes.length > 0
                }
                onChange={(e) =>
                  e.target.checked
                    ? setValue('paymentMethodType', [...visiblePaymentMethodTypes])
                    : setValue('paymentMethodType', [])
                }
              />
            }
            label="Select All"
          />
          {visiblePaymentMethodTypes.map((paymentMethodType) => (
            <FormControlLabel
              key={paymentMethodType}
              control={
                <Checkbox
                  checked={selectedPaymentMethodTypes.includes(paymentMethodType)}
                  onChange={(e) =>
                    e.target.checked
                      ? setValue('paymentMethodType', [
                          ...selectedPaymentMethodTypes,
                          paymentMethodType,
                        ])
                      : setValue(
                          'paymentMethodType',
                          selectedPaymentMethodTypes.filter(
                            (selectedType) => selectedType !== paymentMethodType,
                          ),
                        )
                  }
                />
              }
              label={getPaymentMethodFilterLabel(paymentMethodType)}
            />
          ))}
          <FormHelperText>Only applies to paylinks that have been paid.</FormHelperText>
        </Section>
      )}
      <DateRangeSection
        title="Creation Date Range"
        names={['createdAfter', 'createdBefore'] as const}
        maxDateRange={maxDateRange}
        hookFormProps={hookFormProps}
      />
      {showTransactionDateRange && (
        <DateRangeSection
          title="Transaction Date Range"
          names={['transactedAfter', 'transactedBefore'] as const}
          maxDateRange={maxDateRange}
          hookFormProps={hookFormProps}
        />
      )}
      <Section title="Amount">
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(['amountGTE', 'amountLTE'] as const).map((name) => (
            <Controller
              key={name}
              name={name}
              control={control}
              render={({ field }) => (
                <NumericFormat
                  key={name}
                  customInput={TextField}
                  variant="outlined"
                  label={name === 'amountGTE' ? 'Minimum' : 'Maximum'}
                  thousandSeparator
                  prefix={acceptedCurrencySymbol}
                  fixedDecimalScale
                  decimalScale={2}
                  allowNegative={false}
                  error={!!errors[name]}
                  helperText={errors[name]?.message}
                  onChange={field.onChange}
                  value={field.value}
                  name={field.name}
                  id={field.name}
                />
              )}
            />
          ))}
        </Box>
      </Section>
      <Section title="Customer (s)">
        <CustomerAutoComplete
          multiple
          inputProps={{
            label: 'Search by name, phone or email',
          }}
          value={selectedCustomers}
          onChange={(_e, value) => {
            setValue('customers', value);
          }}
          renderTags={() => null}
        />
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {selectedCustomers.map((customer) => {
            return (
              <Chip
                sx={{
                  bgcolor: colors.grey[300],
                  height: '35px',
                  px: 0.5,
                  borderRadius: '50px',

                  fontSize: 14,
                  color: colors.common.black,
                  '& .MuiChip-deleteIcon': {
                    color: colors.grey[900],
                  },
                }}
                deleteIcon={<Close sx={{ fontSize: 16 }} />}
                key={customer.payLinkCustomerId}
                label={getOptionLabel(customer)}
                onDelete={() => {
                  setValue(
                    'customers',
                    selectedCustomers.filter(
                      (c) => c.payLinkCustomerId !== customer.payLinkCustomerId,
                    ),
                  );
                }}
              />
            );
          })}
        </Box>
      </Section>
    </>
  );
};
